import { Button } from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useReturnRequest } from "@babackapp/return-widget-components/queries/use-return-requests";
import type { ReturnRequest } from "@babackapp/return-widget-components/types";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { ArrowLeft } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { isCancellable } from "../../utils/is-cancellable";
import { getCarrierRelayUrl } from "../../utils/carrier-relay-url";
import { resolveReturnLocation } from "../../utils/resolve-return-method";
import { resolveEndProcessTexts } from "../../utils/end-process-texts";
import { ReturnContent } from "./return-content";
import { ReturnStatusTimeline } from "./return-status-timeline";
import { StatusAlert } from "./status-alert";
import { ReturnMethodSection } from "./return-method-section";
import { CancelReturnSection } from "./cancel-return-section";

export function ReturnRequestDetails() {
  const { orderId, shopSlug, returnRequestId } = useWidgetParams();
  const { data: returnRequest } = useReturnRequest({
    orderId,
    shopSlug,
    returnRequestId: returnRequestId!,
  });

  // The public search excludes cancelled returns: a missing return request
  // means it was cancelled (here, from another tab, or by the merchant).
  if (!returnRequest) {
    return <Navigate to="../cancel-complete" replace />;
  }

  return <ReturnRequestDetailsContent returnRequest={returnRequest} />;
}

function ReturnRequestDetailsContent({
  returnRequest,
}: {
  returnRequest: ReturnRequest;
}) {
  const { orderId, shopId, logoUrl } = useWidgetParams();
  const t = useCustomText();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);

  const canCancel =
    shop.preferences.canCancelReturnRequest &&
    isCancellable(returnRequest.status);

  const returnStore = shop.returnToStore.stores.find(
    (s) => s.id === returnRequest.shipping_info.returnStoreId,
  );

  const returnLocation = resolveReturnLocation(
    shop.locations,
    returnRequest.shipping_info.locationId,
  );

  const shippingAddress = (order as { shippingAddress?: unknown })?.shippingAddress ?? null;
  const isStoreReturn = !!returnRequest.shipping_info.returnStoreId;
  const relayUrl =
    !isStoreReturn && shippingAddress
      ? getCarrierRelayUrl(returnRequest.shipping_info.carrier, shippingAddress as Parameters<typeof getCarrierRelayUrl>[1])
      : null;

  return (
    <WidgetPage className="pt-10 pb-8">
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>
          <Link to="..">
            <Button variant="ghost">
              <ArrowLeft />
            </Button>
          </Link>
          {t("returnRequests.details.title")}
        </HeaderTitle>
        <HeaderDescription>
          {t("returnRequests.details.description")}
        </HeaderDescription>
      </Header>

      <UserPortalCallout
        texts={resolveEndProcessTexts(
          shop.preferences.userPortal,
          returnRequest.shipping_info.carrier,
        )}
        returnRequest={returnRequest}
      />
      <ReturnContent returnRequest={returnRequest} />
      <StatusAlert status={returnRequest.status} shippingInfo={returnRequest.shipping_info} relayUrl={relayUrl} />
      <ReturnStatusTimeline status={returnRequest.status} shippingInfo={returnRequest.shipping_info} />
      <ReturnMethodSection
        shippingInfo={returnRequest.shipping_info}
        returnStore={returnStore}
        location={returnLocation}
      />
      {canCancel && <CancelReturnSection returnRequestId={returnRequest.id} />}
    </WidgetPage>
  );
}
